// Simple Slider Web Component

if (!customElements.get('simple-slider')) {
  class SimpleSlider extends HTMLElement {
    constructor() {
      super();

      // Slider elements
      this.sliderList = this.querySelector('[data-list]');
      this.slideLeft = this.querySelector('[data-slide-left]');
      this.slideRight = this.querySelector('[data-slide-right]');

      // Initial state
      this.slide = this.sliderList?.children[0] || null;
      this.currentIndex = 0;
      this.isPointerDown = false;
      this.startX = 0;
      this.scrollLeftStart = 0;
      this.dragged = false; // Track whether dragging occurred
      this.hasScrolled = false; // Ensure only one scroll per click
      this.size = this.sliderList.children.length;
      this.isSafari = navigator.userAgent.includes('Safari') && !navigator.userAgent.includes('Chrome');
      this._hasInitialIntersection = false;

      if (this.isSafari) {
        this.sensitivity = 1.5; // Adjust sensitivity for Safari
      } else {
        this.sensitivity = 1;
      }

      this.setupEventListeners();
      this.setupObservers();
      this.setupOpacityOnScroll();
      this.initializeToFirstSlide();
    }

    // Ensure slider state and position are consistently reset to the first slide
    initializeToFirstSlide() {
      if (!this.sliderList) return;

      // Force scroll position to the very beginning
      this.sliderList.scrollLeft = 0;

      // Make sure internal state matches the first slide
      this.currentIndex = 0;
      this.visibilityNavigation();

      // Run again after full page load to override any browser scroll restoration
      window.addEventListener('load', () => {
        this.sliderList.scrollLeft = 0;
        this.currentIndex = 0;
        this.visibilityNavigation();
      }, { once: true });
    }

    setupEventListeners() {
      this.slideLeft?.addEventListener('click', () => this.scrollSliderLeft());
      this.slideRight?.addEventListener('click', () => this.scrollSliderRight());

      if (window.innerWidth >= 1024) {
        // Pointer (Mouse & Touch) drag support
        this.sliderList.addEventListener('pointerdown', this.onPointerDown.bind(this));
        this.sliderList.addEventListener('pointermove', this.onPointerMove.bind(this));
        this.sliderList.addEventListener('pointerup', this.onPointerUp.bind(this));
        this.sliderList.addEventListener('pointerleave', this.onPointerUp.bind(this));

        // Prevent link navigation on drag
        this.sliderList.querySelectorAll('a').forEach((link) => {
          link.addEventListener('click', (event) => {
            if (this.dragged) {
              event.preventDefault();
            }
          });
        });
      }
    }

    onPointerDown(event) {
      this.isPointerDown = true;
      this.startX = event.pageX;
      this.scrollLeftStart = this.sliderList.scrollLeft;
      this.dragged = false; // Reset dragged state on new drag
      this.hasScrolled = false; // Allow scrolling again
      this.sliderList.style.cursor = 'grabbing'; // Change cursor to indicate dragging
      event.preventDefault(); // Prevent unintended selection
    }

    onPointerMove(event) {
      if (!this.isPointerDown || this.hasScrolled) return;
      const moveX = (event.pageX - this.startX) * this.sensitivity;

      if (Math.abs(moveX) > 2) { // Reduced sensitivity for quicker response
        this.dragged = true;
      }

      if (Math.abs(moveX) > 20) { // Set threshold for one-time scroll
        if (moveX > 0) {
          this.scrollSliderLeft();
        } else {
          this.scrollSliderRight();
        }
        this.hasScrolled = true; // Prevent further scrolling until next click
      }
    }

    onPointerUp() {
      this.isPointerDown = false;
      this.sliderList.style.cursor = 'grab'; // Reset cursor
    }

    scrollSliderLeft() {
      this.sliderList.scrollLeft -= this.slide.clientWidth;
    }

    scrollSliderRight() {
      this.sliderList.scrollLeft += this.slide.clientWidth;
    }

    setupObservers() {
      // Intersection Observer for slides
      const threshold = 0.8;
      const rootMargin = `0px 0px 0px 0px`;
      const observerOptions = {
        threshold,
        rootMargin,
      };

      const sliderScrollObserver = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (!entry.isIntersecting) return;

          const index = parseInt(entry.target.dataset.index, 10);

          // On initial load, we want to stick to the first slide (index 0)
          // and ignore any transient intersections of other slides.
          if (!this._hasInitialIntersection) {
            if (index !== 0) {
              return;
            }
            this._hasInitialIntersection = true;
          }

          this.currentIndex = index;
          this.visibilityNavigation();
          console.log("Current index:", this.currentIndex, 'Slides per view:', this.getSlidesPerView());
        });
      }, observerOptions);

      [...this.sliderList.children].forEach((slide, index) => {
        slide.dataset.index = index;
        sliderScrollObserver.observe(slide);
      });
    }


    getSlidesPerView() {
      const sliderContainer = this.sliderList;
      const slides = [...this.sliderList.children];

      if (!sliderContainer || slides.length === 0) return 1;

      const containerWidth = sliderContainer.clientWidth;
      const slideWidth = slides[0].clientWidth;
      const firstSlideOffset = slides[0].offsetLeft; // Distance from the left edge of the container
      const gap = 24; // Fixed gap between slides

      // Subtract the gap for all but the last slide
      const adjustedWidth = containerWidth - firstSlideOffset;

      // Calculate the number of slides per view, including the gap
      const slidesPerView = Math.max(1, Math.floor((adjustedWidth + gap) / (slideWidth + gap)));

      return slidesPerView;
    }

    setupOpacityOnScroll() {
      if (!this.sliderList) return;
      this.updateSlideOpacity();
      this.sliderList.addEventListener('scroll', () => {
        requestAnimationFrame(() => this.updateSlideOpacity());
      }, { passive: true });
    }

    updateSlideOpacity() {
      const container = this.sliderList;
      const scrollLeft = container.scrollLeft;
      const containerWidth = container.clientWidth;

      [...container.children].forEach((slide) => {
        const slideLeft = slide.offsetLeft - container.offsetLeft;
        const slideWidth = slide.clientWidth;

        // How much of the slide is visible (0 to 1)
        const visibleStart = Math.max(0, scrollLeft - slideLeft);
        const visibleEnd = Math.max(0, (slideLeft + slideWidth) - (scrollLeft + containerWidth));
        const hiddenPortion = visibleStart + visibleEnd;
        const visibilityRatio = Math.max(0, 1 - hiddenPortion / slideWidth);

        // Power curve makes opacity drop off faster as the slide starts leaving
        slide.style.opacity = Math.max(0, Math.pow(visibilityRatio, 6));
      });
    }

    visibilityNavigation() {
      if (this.currentIndex === 0) {
        this.slideLeft?.setAttribute('data-active', 'false');
      } else {
        this.slideLeft?.setAttribute('data-active', 'true');
      }

      if (this.currentIndex >= (this.size - this.getSlidesPerView())) {
        this.slideRight?.setAttribute('data-active', 'false');
      } else {
        this.slideRight?.setAttribute('data-active', 'true');
      }
    }
  }

  customElements.define('simple-slider', SimpleSlider);
}
